import { isEqual } from "lodash";

import { flatten } from "@nens/shared-utils";

import { FileDataTypeEnum } from "@/lib/rana-file-data-type";

import { CompleteFile } from "../types/file-reference-with-file";
import { Folder, Layer, PublicationMap, StorePublication } from "../types/publication";
import { PropertTypeToLayerTypeMap, isTypedLayer } from "../utils/is-layer-typed";

import { getNewVersionCreateFromPublication } from "./transformers";

export function getId(store: StorePublication) {
  return store.id;
}

export function getName(store: StorePublication) {
  return store.name;
}

export function getDescription(store: StorePublication) {
  return store.description;
}

export function getIsPublic(store: StorePublication) {
  return store.isPublic;
}

export function getMaps(store: StorePublication) {
  return store.maps;
}

export function getFiles(store: StorePublication) {
  return store.files;
}

// Find isChanged by comparing the current data to the initial data converted to the format expected by the API.
// So stripping all the UI fields
export function getIsChanged(store: StorePublication) {
  return !isEqual(
    store._initialPublicationVersionCreate,
    getNewVersionCreateFromPublication(store)
  );
}

export function getCreator(store: StorePublication) {
  return store.creator;
}

export function getProjectId(store: StorePublication) {
  return store.projectId;
}

export function getProjectSlug(store: StorePublication) {
  return store.projectSlug;
}

export function getOpenMap(store: StorePublication): PublicationMap {
  const maps = getMaps(store);
  const openMapId = store.openMap;
  return maps.find((map) => map.id === openMapId) || maps[0];
}

export function getOpenMapStudyArea(store: StorePublication): Layer | null {
  return getOpenMap(store).studyArea;
}

export function getFileByPath(filePath: string) {
  return (store: StorePublication): CompleteFile | null => {
    const files = getFiles(store);
    return files.find((file) => file.path === filePath) || null;
  };
}

export function isLayerInFolder(layerId: string) {
  // Returns a boolean that says whether the layer with the given id is inside a folder of the currently open map
  return (store: StorePublication): boolean => {
    const openMap = getOpenMap(store);
    return openMap.layers.some(
      (layer) =>
        layer.type === "folder" &&
        layer.layers.some((sublayer) => sublayer.type === "layer" && sublayer.id === layerId)
    );
  };
}

export function getFolders(store: StorePublication): Folder[] {
  const openMap = getOpenMap(store);
  return openMap.layers.filter((layer) => layer.type === "folder");
}

export function getAllLayers(store: StorePublication): Layer[] {
  // Get all layers from the currently open map and return them as a flat array.
  const openMap = getOpenMap(store);
  const mapLayers = flatten(
    openMap.layers.map(
      (layer) => (layer.type === "folder" ? layer.layers : layer) as Layer | Layer[]
    )
  );
  return openMap.studyArea ? [...mapLayers, openMap.studyArea] : mapLayers;
}

export function getLayersOfType<Type extends FileDataTypeEnum>(type: Type) {
  type TypedLayer = PropertTypeToLayerTypeMap[Type];
  return (store: StorePublication): TypedLayer[] => {
    const allLayers = getAllLayers(store);

    return allLayers.filter((layer): layer is TypedLayer => isTypedLayer(layer, type));
  };
}

export function getVisibleLayersOfType<Type extends FileDataTypeEnum>(type: Type) {
  return (store: StorePublication) => {
    return getLayersOfType<Type>(type)(store).filter((layer) => layer.visible);
  };
}

export function getIsEditable(store: StorePublication): boolean {
  // Publications are editable if:
  // - We are in draft mode
  // - OR we are in published mode and isEditing is true
  // - AND we are not in public mode
  if (store.isPublic) return false;

  if (getPublishedVersion(store)) return store.isEditing;

  return true;
}

export function getIsSaveable(store: StorePublication): boolean {
  // Publications can be saved if they are editable and there are
  // changes.
  return getIsEditable(store) && getIsChanged(store);
}

export function getVersion(store: StorePublication) {
  return store.version;
}

export function getLatestVersion(store: StorePublication) {
  return store.latestVersion;
}

// Returns epoch milliseconds rather than a Date. usePublicationStore compares selector
// results with zustand's shallow(), which treats any two Dates as equal because a Date has
// no own enumerable keys. Returning a Date here would mean the value never updates after
// the first render, so it would only refresh on remount.
export function getLastSaveTime(store: StorePublication) {
  return store.lastSave.getTime();
}

export function getPublishedVersion(store: StorePublication) {
  return store.publishedVersion;
}

export function getSelectedLayers(store: StorePublication): Layer[] {
  // Flatten all selected layers, including those in folders
  return getAllLayers(store).filter((layer) => layer.selected);
}

export function getIsPublishedVersion(store: StorePublication): boolean {
  return store.publishedVersion === store.version;
}

export function getIsDraftVersion(store: StorePublication): boolean {
  return store.latestVersion === store.version;
}

export function getIsPublishable(store: StorePublication): boolean {
  // We can't publish if we are already in public mode.
  if (store.isPublic) return false;
  // We can't publish if there are changes
  if (getIsChanged(store)) return false;
  // We can't publish if no version has been saved yet
  if (store.version === 0) return false;
  // We can't publish if this version is already published
  if (store.publishedVersion === store.version) return false;
  // Otherwise, seems we can.
  return true;
}
